/**
 * Onglet Réglages — boutique, identité, sync, hors-ligne.
 */
import { useEffect, useState } from 'react';
import { db } from '../lib/db';
import { syncPull, enLigne, login, inscription } from '../lib/api';

interface IdentiteBoutique {
  nom: string;
  tel: string;
  quartier: string;
  userNom: string;
}

export function Reglages() {
  const [identite, setIdentite] = useState<IdentiteBoutique>(() => {
    try {
      return JSON.parse(localStorage.getItem('boutikos.identite') ?? '{}');
    } catch {
      return { nom: '', tel: '', quartier: '', userNom: '' };
    }
  });
  const [enLigneNow, setEnLigneNow] = useState(enLigne());
  const [syncEnCours, setSyncEnCours] = useState(false);
  const [compte, setCompte] = useState<'invite' | 'inscrit'>('invite');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const on = () => setEnLigneNow(true);
    const off = () => setEnLigneNow(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    if (localStorage.getItem('boutikos.jwt')) setCompte('inscrit');
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const sauver = () => {
    localStorage.setItem('boutikos.identite', JSON.stringify(identite));
    setMessage('✅ Identité sauvegardée');
    setTimeout(() => setMessage(null), 2000);
  };

  const forcerSync = async () => {
    setSyncEnCours(true);
    try {
      const r = await syncPull();
      setMessage(`✅ ${r.applied} opération(s) synchronisée(s)`);
    } catch (e) {
      setMessage(`❌ Sync échouée : ${(e as Error).message}`);
    } finally {
      setSyncEnCours(false);
    }
  };

  const creerCompte = async () => {
    if (!identite.tel || !identite.userNom || !identite.nom) {
      setMessage('Nom, téléphone et nom utilisateur requis');
      return;
    }
    const pin = prompt('PIN à 4-6 chiffres :') ?? '';
    if (!/^\d{4,6}$/.test(pin)) {
      setMessage('PIN invalide');
      return;
    }
    try {
      await inscription({
        tel: identite.tel,
        nom_boutique: identite.nom,
        nom_user: identite.userNom,
        pin,
        quartier: identite.quartier || undefined,
      });
      setCompte('inscrit');
      setMessage('✅ Boutique créée. Tu peux maintenant synchroniser.');
    } catch (e) {
      setMessage(`❌ ${(e as Error).message}`);
    }
  };

  const seConnecter = async () => {
    const pin = prompt('PIN :') ?? '';
    if (!pin) return;
    try {
      await login({ tel: identite.tel, pin });
      setCompte('inscrit');
      setMessage('✅ Connecté');
    } catch (e) {
      setMessage(`❌ ${(e as Error).message}`);
    }
  };

  return (
    <>
      <h1 className="titre-section">⚙️ Réglages</h1>

      {message && (
        <div className="carte" style={{ background: '#ecfdf5', borderColor: '#a7f3d0' }}>{message}</div>
      )}

      <section className="carte">
        <h3>🟢 Réseau & sync</h3>
        <div className="ligne">
          <span>État</span>
          <span className={`badge ${enLigneNow ? 'ouvert' : 'attente'}`}>
            {enLigneNow ? 'En ligne' : 'Hors ligne'}
          </span>
        </div>
        <div className="ligne">
          <span>Compte serveur</span>
          <span className={`badge ${compte === 'inscrit' ? 'ouvert' : 'attente'}`}>
            {compte === 'inscrit' ? 'Inscrit' : 'Invité (local)'}
          </span>
        </div>
        <button
          className="bouton bouton-primaire bouton-pleine-largeur"
          onClick={forcerSync}
          disabled={syncEnCours || !enLigneNow || compte === 'invite'}
          style={{ marginTop: 12 }}
        >
          {syncEnCours ? '⏳ Sync…' : '🔄 Synchroniser maintenant'}
        </button>
      </section>

      {compte === 'invite' ? (
        <section className="carte">
          <h3>🏪 Créer ma boutique</h3>
          <Champ label="Nom de la boutique" value={identite.nom} onChange={(v) => setIdentite((i) => ({ ...i, nom: v }))} />
          <Champ label="Quartier" value={identite.quartier} onChange={(v) => setIdentite((i) => ({ ...i, quartier: v }))} />
          <Champ label="Téléphone (numéro principal)" value={identite.tel} onChange={(v) => setIdentite((i) => ({ ...i, tel: v }))} type="tel" />
          <Champ label="Ton prénom (patron)" value={identite.userNom} onChange={(v) => setIdentite((i) => ({ ...i, userNom: v }))} />
          <button className="bouton bouton-primaire bouton-pleine-largeur" onClick={creerCompte}>
            Créer mon compte
          </button>
        </section>
      ) : (
        <section className="carte">
          <h3>🔐 Connexion</h3>
          <button className="bouton bouton-secondaire bouton-pleine-largeur" onClick={seConnecter}>
            Se reconnecter (autre appareil)
          </button>
        </section>
      )}

      <section className="carte">
        <h3>🏪 Ma boutique</h3>
        <Champ label="Nom" value={identite.nom} onChange={(v) => setIdentite((i) => ({ ...i, nom: v }))} />
        <Champ label="Quartier" value={identite.quartier} onChange={(v) => setIdentite((i) => ({ ...i, quartier: v }))} />
        <button className="bouton bouton-secondaire bouton-pleine-largeur" onClick={sauver}>
          💾 Enregistrer
        </button>
      </section>

      <section className="carte" style={{ fontSize: 13, color: 'var(--couleur-texte-doux)' }}>
        <h3>À propos</h3>
        <p><strong>BoutikOS</strong> — L'OS du commerçant africain.</p>
        <p>Offline-first. Tes données restent sur ton téléphone. La synchro vers le cloud est facultative.</p>
      </section>
    </>
  );
}

function Champ(props: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--couleur-texte-doux)' }}>{props.label}</div>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--couleur-bordure)' }}
      />
    </label>
  );
}
